import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ClipboardList, Download, Eye, Pencil, Plus, Save, Settings, Upload, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  Organization,
  ProductBrand,
  SurveyChartColors,
  SurveyQuestionDefinition,
  SurveyQuestionKind,
  SurveyReportingPreset,
  SurveyReportChartType,
  SurveyTemplate,
  SurveyTemplateVersion,
} from '../../types/domain';

type TemplateDraft = Pick<SurveyTemplate, 'client' | 'name' | 'slug' | 'description' | 'reporting_preset' | 'is_active'> & { brand: string };
type QuestionDraft = Omit<SurveyQuestionDefinition, 'id' | 'position'> & { choices_text: string; condition_question: string; condition_value: string };
type WordingCorrectionTarget = { version: SurveyTemplateVersion; question: SurveyQuestionDefinition };
type WordingCorrectionDraft = { corrected_label: string; corrected_help_text: string; reason: string };
type PresetDraft = Pick<SurveyReportingPreset, 'name' | 'description' | 'availability_chart_type' | 'question_chart_type' | 'chart_colors' | 'is_active'> & {
  reporting_groups: string;
  availability_sections: string;
};

const defaultSurveyChartColors: SurveyChartColors = {
  coverage_by_city: '#2F8065',
  question_default: '#4F7FA8',
  availability_by_city: {
    premium_comfort_presence: '#2F8065',
    savers_presence: '#D9A72F',
    known_competitor_category_presence: '#78847D',
  },
  availability_sections: {},
  question_options: {
    available: '#2F8065',
    not_available: '#78847D',
    answered: '#4F7FA8',
  },
};

function resolvedChartColors(colors: SurveyChartColors | undefined): SurveyChartColors {
  return {
    ...defaultSurveyChartColors,
    ...colors,
    availability_by_city: {
      ...defaultSurveyChartColors.availability_by_city,
      ...colors?.availability_by_city,
    },
    availability_sections: {
      ...defaultSurveyChartColors.availability_sections,
      ...colors?.availability_sections,
    },
    question_options: {
      ...defaultSurveyChartColors.question_options,
      ...colors?.question_options,
    },
  };
}

function ChartColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const safeValue = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#2F8065';
  return <label>{label}<span className="brand-theme-colour-inputs"><input aria-label={`${label} colour picker`} onChange={(event) => onChange(event.target.value.toUpperCase())} type="color" value={safeValue} /><input aria-label={`${label} colour`} maxLength={7} onChange={(event) => onChange(event.target.value.toUpperCase())} pattern="#[0-9A-Fa-f]{6}" required type="text" value={value} /></span></label>;
}

function ReportingPresetColourFields({
  draft,
  onChange,
}: {
  draft: PresetDraft;
  onChange: (colors: SurveyChartColors) => void;
}) {
  let availabilitySections: Array<{ key: string; label: string }> = [];
  try {
    const parsed: unknown = JSON.parse(draft.availability_sections);
    if (Array.isArray(parsed)) {
      availabilitySections = parsed.filter((section): section is { key: string; label: string } => (
        Boolean(section)
        && typeof section === 'object'
        && typeof (section as { key?: unknown }).key === 'string'
        && typeof (section as { label?: unknown }).label === 'string'
      ));
    }
  } catch {
    availabilitySections = [];
  }
  const setScalar = (key: 'coverage_by_city' | 'question_default', color: string) => {
    onChange({ ...draft.chart_colors, [key]: color });
  };
  const setMapped = (
    group: 'availability_by_city' | 'availability_sections' | 'question_options',
    key: string,
    color: string,
  ) => {
    onChange({
      ...draft.chart_colors,
      [group]: { ...draft.chart_colors[group], [key]: color },
    });
  };

  return <fieldset className="form-grid-full survey-chart-colour-settings"><legend>Chart colours</legend><p>Colours are stored against stable reporting keys, so they remain consistent across filters and chart types.</p><div className="form-grid"><ChartColourField label="Coverage by city" value={draft.chart_colors.coverage_by_city} onChange={(color) => setScalar('coverage_by_city', color)} /><ChartColourField label="Question default" value={draft.chart_colors.question_default} onChange={(color) => setScalar('question_default', color)} /><ChartColourField label="Available answers" value={draft.chart_colors.question_options.available ?? draft.chart_colors.question_default} onChange={(color) => setMapped('question_options', 'available', color)} /><ChartColourField label="Not available answers" value={draft.chart_colors.question_options.not_available ?? draft.chart_colors.question_default} onChange={(color) => setMapped('question_options', 'not_available', color)} /><ChartColourField label="Answered responses" value={draft.chart_colors.question_options.answered ?? draft.chart_colors.question_default} onChange={(color) => setMapped('question_options', 'answered', color)} /><ChartColourField label="Premium & Comfort by city" value={draft.chart_colors.availability_by_city.premium_comfort_presence ?? draft.chart_colors.coverage_by_city} onChange={(color) => setMapped('availability_by_city', 'premium_comfort_presence', color)} /><ChartColourField label="Savers by city" value={draft.chart_colors.availability_by_city.savers_presence ?? draft.chart_colors.coverage_by_city} onChange={(color) => setMapped('availability_by_city', 'savers_presence', color)} /><ChartColourField label="Known competitors by city" value={draft.chart_colors.availability_by_city.known_competitor_category_presence ?? draft.chart_colors.coverage_by_city} onChange={(color) => setMapped('availability_by_city', 'known_competitor_category_presence', color)} /></div>{availabilitySections.length ? <><h4>Availability mix sections</h4><div className="form-grid">{availabilitySections.map((section) => <ChartColourField key={section.key} label={section.label} value={draft.chart_colors.availability_sections[section.key] ?? draft.chart_colors.coverage_by_city} onChange={(color) => setMapped('availability_sections', section.key, color)} />)}</div></> : null}</fieldset>;
}

const questionKinds: Array<{ value: SurveyQuestionKind; label: string }> = [
  { value: 'yes_no', label: 'Yes / no' },
  { value: 'single_select', label: 'Select one' },
  { value: 'multi_select', label: 'Select many' },
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long notes' },
  { value: 'number', label: 'Number' },
  { value: 'phone', label: 'Phone / contact' },
  { value: 'photo', label: 'Photo evidence' },
  { value: 'location', label: 'Location evidence' },
];

const surveyQuestionCsvHeaders = [
  'stable_key', 'section', 'question', 'help_text', 'answer_type', 'required', 'options',
  'show_when_question', 'show_when_equals',
];

function parseCsv(text: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          value += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        value += character;
      }
      continue;
    }
    if (character === '"') {
      if (value) throw new Error('A quote must begin at the start of a CSV value.');
      quoted = true;
    } else if (character === ',') {
      row.push(value);
      value = '';
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }
  if (quoted) throw new Error('The CSV has an unclosed quoted value.');
  if (value || row.length) {
    row.push(value);
    rows.push(row);
  }
  return rows;
}

function parseCsvChoices(value: string, rowNumber: number) {
  const choices = value.split(';').map((choice) => choice.trim()).filter(Boolean).map((choice) => {
    const [choiceValue = '', ...labelParts] = choice.split('|');
    const optionValue = choiceValue.trim();
    const optionLabel = labelParts.join('|').trim();
    if (!optionValue || !optionLabel) {
      throw new Error(`Row ${rowNumber}: each option must use value | label.`);
    }
    return { value: optionValue, label: optionLabel };
  });
  if (choices.length !== new Set(choices.map((choice) => choice.value)).size) {
    throw new Error(`Row ${rowNumber}: option values must be unique.`);
  }
  return choices;
}

function parseSurveyQuestionCsv(text: string): QuestionDraft[] {
  const [headers = [], ...rawRows] = parseCsv(text.replace(/^\uFEFF/, ''));
  if (headers.length !== surveyQuestionCsvHeaders.length || headers.some((header, index) => header !== surveyQuestionCsvHeaders[index])) {
    throw new Error('CSV columns do not match the BFC survey-question template. Download a fresh template.');
  }
  const validKinds = new Set(questionKinds.map((kind) => kind.value));
  const questions: QuestionDraft[] = [];
  const keys = new Set<string>();
  rawRows.forEach((rawRow, index) => {
    const rowNumber = index + 2;
    if (!rawRow.some((value) => value.trim())) return;
    if (rawRow.length !== surveyQuestionCsvHeaders.length) {
      throw new Error(`Row ${rowNumber}: expected ${surveyQuestionCsvHeaders.length} columns.`);
    }
    const [
      stable_key = '', section = '', question = '', help_text = '', answer_type = '', required = '',
      options = '', show_when_question = '', show_when_equals = '',
    ] = rawRow.map((value) => value.trim());
    const data = {
      stable_key, section, question, help_text, answer_type, required, options,
      show_when_question, show_when_equals,
    };
    if (!/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(data.stable_key)) {
      throw new Error(`Row ${rowNumber}: stable_key must use lowercase letters, numbers, and underscores.`);
    }
    if (keys.has(data.stable_key)) throw new Error(`Row ${rowNumber}: stable_key must be unique.`);
    keys.add(data.stable_key);
    if (!data.question) throw new Error(`Row ${rowNumber}: question is required.`);
    if (!validKinds.has(data.answer_type as SurveyQuestionKind)) {
      throw new Error(`Row ${rowNumber}: answer_type is not supported.`);
    }
    if (!['true', 'false'].includes(data.required.toLowerCase())) {
      throw new Error(`Row ${rowNumber}: required must be true or false.`);
    }
    const kind = data.answer_type as SurveyQuestionKind;
    const choices = data.options ? parseCsvChoices(data.options, rowNumber) : [];
    if (isSelect(kind) && !choices.length) {
      throw new Error(`Row ${rowNumber}: select questions need at least one option.`);
    }
    if (!isSelect(kind) && choices.length) {
      throw new Error(`Row ${rowNumber}: only select questions may have options.`);
    }
    if (Boolean(data.show_when_question) !== Boolean(data.show_when_equals)) {
      throw new Error(`Row ${rowNumber}: complete both conditional display columns or leave both blank.`);
    }
    const controller = data.show_when_question;
    if (controller && !questions.some((question) => question.key === controller)) {
      throw new Error(`Row ${rowNumber}: conditional display must reference an earlier stable_key.`);
    }
    questions.push({
      key: data.stable_key,
      section: data.section,
      label: data.question,
      help_text: data.help_text,
      kind,
      required: data.required.toLowerCase() === 'true',
      choices: [],
      visibility_condition: {},
      choices_text: choices.map((choice) => `${choice.value} | ${choice.label}`).join('\n'),
      condition_question: controller,
      condition_value: data.show_when_equals,
    });
  });
  if (!questions.length) throw new Error('The CSV does not contain any question rows.');
  return questions;
}

const emptyTemplateDraft: TemplateDraft = {
  client: '', brand: '', name: '', slug: '', description: '', reporting_preset: '', is_active: true,
};

function emptyQuestion(position: number): QuestionDraft {
  return {
    key: `question_${position}`,
    section: 'Survey',
    label: '',
    help_text: '',
    kind: 'yes_no',
    required: true,
    choices: [],
    visibility_condition: {},
    choices_text: '',
    condition_question: '',
    condition_value: '',
  };
}

function questionDraft(question: SurveyQuestionDefinition): QuestionDraft {
  const condition = question.visibility_condition as { question?: unknown; equals?: unknown };
  const expected = condition.equals;
  return {
    ...question,
    choices_text: question.choices.map((choice) => `${choice.value} | ${choice.label}`).join('\n'),
    condition_question: typeof condition.question === 'string' ? condition.question : '',
    condition_value: typeof expected === 'string' || typeof expected === 'number' || typeof expected === 'boolean' ? String(expected) : '',
  };
}

function isSelect(kind: SurveyQuestionKind) {
  return kind === 'single_select' || kind === 'multi_select';
}

function questionKindLabel(kind: SurveyQuestionKind) {
  return questionKinds.find((candidate) => candidate.value === kind)?.label ?? kind;
}

function visibilityDescription(question: SurveyQuestionDefinition, questions: SurveyQuestionDefinition[]) {
  const condition = question.visibility_condition as { question?: unknown; equals?: unknown };
  if (typeof condition.question !== 'string' || condition.equals === undefined) return null;
  const controller = questions.find((candidate) => candidate.key === condition.question);
  const expected = typeof condition.equals === 'string'
    ? condition.equals
    : JSON.stringify(condition.equals);
  return `Shown when “${controller?.label || condition.question}” equals “${expected}”`;
}

function parseChoices(value: string) {
  return value.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const [choiceValue = '', ...labelParts] = line.split('|');
    return { value: choiceValue.trim(), label: labelParts.join('|').trim() || choiceValue.trim() };
  });
}

function parseJsonArray(value: string, label: string): unknown[] {
  const parsed: unknown = JSON.parse(value);
  if (!Array.isArray(parsed)) throw new Error(`${label} must be a JSON array.`);
  return parsed;
}

function conditionValue(question: QuestionDraft, questions: QuestionDraft[]) {
  const value = question.condition_value.trim();
  const controller = questions.find((candidate) => candidate.key === question.condition_question);
  if (controller?.kind === 'yes_no') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  if (controller?.kind === 'number' && value !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return value;
}

function submitQuestions(questions: QuestionDraft[]) {
  return questions.map((question, index) => ({
    key: question.key,
    section: question.section,
    label: question.label,
    help_text: question.help_text,
    kind: question.kind,
    required: question.required,
    position: index + 1,
    choices: isSelect(question.kind) ? parseChoices(question.choices_text) : [],
    visibility_condition: question.condition_question && question.condition_value
      ? { question: question.condition_question, equals: conditionValue(question, questions) }
      : {},
  }));
}

export function SurveyTemplatesPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const canManage = capabilities.includes('surveys:manage');
  const [editing, setEditing] = useState<SurveyTemplate | 'new' | null>(null);
  const [draft, setDraft] = useState<TemplateDraft>(emptyTemplateDraft);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<SurveyTemplateVersion | 'new' | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [questions, setQuestions] = useState<QuestionDraft[]>([emptyQuestion(1)]);
  const [questionCsvFile, setQuestionCsvFile] = useState<File | null>(null);
  const [questionCsvError, setQuestionCsvError] = useState<string | null>(null);
  const [questionCsvNotice, setQuestionCsvNotice] = useState<string | null>(null);
  const [correctingQuestion, setCorrectingQuestion] = useState<WordingCorrectionTarget | null>(null);
  const [editingPreset, setEditingPreset] = useState<SurveyReportingPreset | null>(null);
  const [presetDraft, setPresetDraft] = useState<PresetDraft | null>(null);
  const [wordingCorrectionDraft, setWordingCorrectionDraft] = useState<WordingCorrectionDraft>({
    corrected_label: '', corrected_help_text: '', reason: '',
  });

  const templateParams = new URLSearchParams({ page_size: '100', ordering: 'name' });
  if (selectedWorkspace?.type === 'client' && selectedWorkspace.organizationId) templateParams.set('client', selectedWorkspace.organizationId);
  const templates = useApiCollection<SurveyTemplate>(
    'survey-template-authoring', `/survey-templates/?${templateParams.toString()}`, accessToken, canManage,
  );
  const presets = useApiCollection<SurveyReportingPreset>(
    'survey-reporting-presets', '/survey-reporting-presets/?page_size=100&ordering=name', accessToken, canManage,
  );
  const presetOptions = presets.data?.results ?? [];
  const organizations = useApiCollection<Organization>(
    'survey-template-clients', '/organizations/?page_size=100&ordering=name&type=client&status=active', accessToken, canManage,
  );
  const clientOptions = (organizations.data?.results ?? []).filter((organization) => (
    organization.type === 'client' && (isPlatformAdmin || selectedWorkspace?.type !== 'client' || organization.id === selectedWorkspace.organizationId)
  ));
  const brandParams = new URLSearchParams({ page_size: '100', ordering: 'name', is_active: 'true' });
  if (draft.client) brandParams.set('client', draft.client);
  const brands = useApiCollection<ProductBrand>(
    'survey-template-brands', `/product-brands/?${brandParams.toString()}`, accessToken, canManage && Boolean(draft.client),
  );
  const brandOptions = (brands.data?.results ?? []).filter((brand) => brand.client === draft.client);
  const rows = templates.data?.results ?? [];
  const selectedTemplate = rows.find((template) => template.id === selectedTemplateId) ?? null;
  const versions = useQuery({
    queryKey: ['survey-template-versions', selectedTemplateId, accessToken],
    enabled: Boolean(accessToken && selectedTemplateId),
    queryFn: () => apiRequest<SurveyTemplateVersion[]>(
      `/survey-templates/${selectedTemplateId}/versions/`, accessToken ? { accessToken } : {},
    ),
  });
  const selectedVersion = (versions.data ?? []).find((version) => version.id === selectedVersionId)
    ?? versions.data?.[0]
    ?? null;
  const selectedVersionSections = selectedVersion
    ? Array.from([...selectedVersion.questions]
      .sort((left, right) => left.position - right.position)
      .reduce((sections, question) => {
        const section = question.section.trim() || 'Survey';
        const existing = sections.get(section) ?? [];
        existing.push(question);
        sections.set(section, existing);
        return sections;
      }, new Map<string, SurveyQuestionDefinition[]>()))
    : [];

  const templateMutation = useMutation({
    mutationFn: () => apiRequest<SurveyTemplate>(
      editing === 'new' ? '/survey-templates/' : `/survey-templates/${editing?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editing === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify(editing === 'new' ? { ...draft, brand: draft.brand || null } : {
          brand: draft.brand || null,
          name: draft.name,
          slug: draft.slug,
          description: draft.description,
          reporting_preset: draft.reporting_preset,
          is_active: draft.is_active,
        }),
      },
    ),
    onSuccess: async (template) => {
      setEditing(null);
      setDraft(emptyTemplateDraft);
      setSelectedTemplateId(template.id);
      setSelectedVersionId(null);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'survey-template-authoring'] });
    },
  });
  const versionMutation = useMutation({
    mutationFn: () => {
      const payload = { questions: submitQuestions(questions) };
      if (editingVersion === 'new') {
        return apiRequest<SurveyTemplateVersion>('/survey-template-versions/', {
          ...(accessToken ? { accessToken } : {}), method: 'POST', body: JSON.stringify({
            template: selectedTemplateId, notes: versionNotes, ...payload,
          }),
        });
      }
      return apiRequest<SurveyTemplateVersion>(`/survey-template-versions/${editingVersion?.id}/questions/`, {
        ...(accessToken ? { accessToken } : {}), method: 'PUT', body: JSON.stringify(payload),
      });
    },
    onSuccess: async (version) => {
      setEditingVersion(null);
      setSelectedVersionId(version.id);
      setVersionNotes('');
      setQuestions([emptyQuestion(1)]);
      await queryClient.invalidateQueries({ queryKey: ['survey-template-versions', selectedTemplateId] });
    },
  });
  const questionCsvTemplateMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error('Select a survey template first.');
      return downloadApiFile(
        `/survey-templates/${selectedTemplateId}/question-csv-template/`,
        accessToken ? { accessToken } : {},
      );
    },
  });
  const publishMutation = useMutation({
    mutationFn: (version: SurveyTemplateVersion) => apiRequest<SurveyTemplateVersion>(
      `/survey-template-versions/${version.id}/publish/`, {
        ...(accessToken ? { accessToken } : {}), method: 'POST', body: JSON.stringify({}),
      },
    ),
    onSuccess: async (version) => {
      setSelectedVersionId(version.id);
      await queryClient.invalidateQueries({ queryKey: ['survey-template-versions', selectedTemplateId] });
    },
  });
  const wordingCorrectionMutation = useMutation({
    mutationFn: () => {
      if (!correctingQuestion) throw new Error('Select a published question to correct.');
      return apiRequest(
        `/survey-template-versions/${correctingQuestion.version.id}/correct-wording/`,
        {
          ...(accessToken ? { accessToken } : {}),
          method: 'POST',
          body: JSON.stringify({
            question: correctingQuestion.question.id,
            ...wordingCorrectionDraft,
          }),
        },
      );
    },
    onSuccess: async () => {
      setCorrectingQuestion(null);
      setWordingCorrectionDraft({ corrected_label: '', corrected_help_text: '', reason: '' });
      await queryClient.invalidateQueries({ queryKey: ['survey-template-versions', selectedTemplateId] });
    },
  });
  const presetMutation = useMutation({
    mutationFn: () => {
      if (!editingPreset || !presetDraft) throw new Error('Select a reporting preset.');
      return apiRequest<SurveyReportingPreset>(`/survey-reporting-presets/${editingPreset.id}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify({
          ...presetDraft,
          reporting_groups: parseJsonArray(presetDraft.reporting_groups, 'Grouped mappings'),
          availability_sections: parseJsonArray(presetDraft.availability_sections, 'Availability sections'),
        }),
      });
    },
    onSuccess: async () => {
      setEditingPreset(null);
      setPresetDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'survey-reporting-presets'] });
    },
  });

  function beginTemplate(template: SurveyTemplate | 'new') {
    templateMutation.reset();
    setEditing(template);
    setDraft(template === 'new' ? {
      ...emptyTemplateDraft,
      client: selectedWorkspace?.type === 'client' ? selectedWorkspace.organizationId ?? '' : clientOptions[0]?.id ?? '',
    } : {
      client: template.client,
      brand: template.brand ?? '',
      name: template.name,
      slug: template.slug,
      description: template.description,
      reporting_preset: template.reporting_preset,
      is_active: template.is_active,
    });
  }

  function beginVersion(version: SurveyTemplateVersion | 'new') {
    versionMutation.reset();
    questionCsvTemplateMutation.reset();
    setEditingVersion(version);
    setVersionNotes(version === 'new' ? '' : version.notes);
    setQuestions(version === 'new' ? [emptyQuestion(1)] : version.questions.map(questionDraft));
    setQuestionCsvFile(null);
    setQuestionCsvError(null);
    setQuestionCsvNotice(null);
  }

  function beginWordingCorrection(version: SurveyTemplateVersion, question: SurveyQuestionDefinition) {
    wordingCorrectionMutation.reset();
    setCorrectingQuestion({ version, question });
    setWordingCorrectionDraft({
      corrected_label: question.label,
      corrected_help_text: question.help_text,
      reason: '',
    });
  }

  function beginPreset(preset: SurveyReportingPreset) {
    presetMutation.reset();
    setEditingPreset(preset);
    setPresetDraft({
      name: preset.name,
      description: preset.description,
      availability_chart_type: preset.availability_chart_type,
      question_chart_type: preset.question_chart_type,
      reporting_groups: JSON.stringify(preset.reporting_groups, null, 2),
      availability_sections: JSON.stringify(preset.availability_sections, null, 2),
      chart_colors: resolvedChartColors(preset.chart_colors),
      is_active: preset.is_active,
    });
  }

  function closeWordingCorrection() {
    if (wordingCorrectionMutation.isPending) return;
    setCorrectingQuestion(null);
    setWordingCorrectionDraft({ corrected_label: '', corrected_help_text: '', reason: '' });
    wordingCorrectionMutation.reset();
  }

  function submitWordingCorrection(event: FormEvent) {
    event.preventDefault();
    wordingCorrectionMutation.mutate();
  }

  async function loadQuestionCsv() {
    if (!questionCsvFile) return;
    setQuestionCsvError(null);
    setQuestionCsvNotice(null);
    try {
      const parsedQuestions = parseSurveyQuestionCsv(await questionCsvFile.text());
      setQuestions(parsedQuestions);
      setQuestionCsvNotice(`Loaded ${parsedQuestions.length} questions from ${questionCsvFile.name}. Review the draft, then save it.`);
    } catch (error) {
      setQuestionCsvError(error instanceof Error ? error.message : 'Unable to read the survey-question CSV.');
    }
  }

  function updateQuestion(index: number, changes: Partial<QuestionDraft>) {
    setQuestions((current) => current.map((question, questionIndex) => (
      questionIndex === index ? { ...question, ...changes } : question
    )));
  }

  function submitTemplate(event: FormEvent) {
    event.preventDefault();
    templateMutation.mutate();
  }

  function submitVersion(event: FormEvent) {
    event.preventDefault();
    versionMutation.mutate();
  }

  return <>
    <header className="page-heading"><div><p className="eyebrow">Survey configuration</p><h1>Survey templates</h1><p>Draft reusable client questionnaires. A platform administrator publishes an immutable version before a campaign can use it.</p></div><span className="record-count"><ClipboardList size={16} /> {templates.data?.count ?? 0} templates</span></header>
    <div className="catalogue-list-toolbar"><p className="inline-help">Templates and drafts remain client-scoped; publication is the final platform quality gate.</p><div className="section-actions">{isPlatformAdmin && presetOptions.length ? <button className="button secondary" onClick={() => { const preset = presetOptions[0]; if (preset) beginPreset(preset); }} type="button"><Settings size={17} /> Reporting presets</button> : null}{canManage && clientOptions.length ? <button className="button primary" onClick={() => beginTemplate('new')} type="button"><Plus size={17} /> New template</button> : null}</div></div>
    <DataPanel error={templates.isError} loading={templates.isLoading} empty={rows.length === 0}>
      <section className="survey-template-grid">
        {rows.map((template) => (
          <article className={`content-card survey-template-card${selectedTemplateId === template.id ? ' selected' : ''}`} key={template.id}>
            <div className="survey-template-card-heading">
              <div>
                <p className="eyebrow">{[template.client_name, template.brand_name || 'All brands'].join(' · ')}</p>
                <h2>{template.name}</h2>
              </div>
              <StatusPill value={template.is_active ? 'active' : 'inactive'} />
            </div>
            <p>{template.description || 'No description provided.'}</p>
            <dl className="survey-template-card-meta">
              <div><dt>Stable slug</dt><dd>{template.slug}</dd></div>
              <div><dt>Reporting</dt><dd>{template.reporting_preset || 'Generic'}</dd></div>
            </dl>
            <div className="survey-template-card-actions">
              <button className="button secondary small" onClick={() => { setSelectedTemplateId(template.id); setSelectedVersionId(null); }} type="button"><Eye size={15} /> View questionnaire</button>
              {canManage ? <button aria-label={`Edit ${template.name}`} className="text-button" onClick={() => beginTemplate(template)} type="button"><Pencil size={14} /> Edit template</button> : null}
            </div>
          </article>
        ))}
      </section>
    </DataPanel>
    {selectedTemplate ? (
      <section className="content-card survey-template-versions">
        <div className="section-heading survey-template-workspace-heading">
          <div>
            <p className="eyebrow">{[selectedTemplate.client_name, selectedTemplate.brand_name].filter(Boolean).join(' · ')}</p>
            <h2>{selectedTemplate.name}</h2>
            <p>Choose a version to inspect its full questionnaire and answer rules.</p>
          </div>
          <div className="section-actions">
            {canManage ? <button className="button primary" onClick={() => beginVersion('new')} type="button"><Plus size={16} /> Draft version</button> : null}
            <button aria-label="Close template versions" className="icon-button" onClick={() => { setSelectedTemplateId(null); setSelectedVersionId(null); }} type="button"><X size={18} /></button>
          </div>
        </div>
        <DataPanel error={versions.isError} loading={versions.isLoading} empty={(versions.data?.length ?? 0) === 0}>
          <div className="survey-template-workspace">
            <aside className="survey-version-browser" aria-label="Questionnaire versions">
              <p className="survey-workspace-label">Versions</p>
              <div className="survey-version-list">
                {(versions.data ?? []).map((version) => (
                  <button
                    aria-pressed={selectedVersion?.id === version.id}
                    className={`survey-version-card${selectedVersion?.id === version.id ? ' selected' : ''}`}
                    key={version.id}
                    onClick={() => setSelectedVersionId(version.id)}
                    type="button"
                  >
                    <span className="survey-version-card-title"><strong>Version {version.version}</strong><StatusPill value={version.status} /></span>
                    <span>{version.notes || 'No version notes.'}</span>
                    <small>{version.questions.length} questions{version.published_at ? ` · Published ${new Intl.DateTimeFormat('en-PK', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(version.published_at))}` : ''}</small>
                    <span className="survey-version-view"><Eye size={14} /> View questions</span>
                  </button>
                ))}
              </div>
            </aside>
            {selectedVersion ? (
              <section className="survey-question-inspector" aria-labelledby="survey-question-inspector-title">
                <div className="survey-question-inspector-heading">
                  <div>
                    <p className="survey-workspace-label">Questionnaire</p>
                    <h3 id="survey-question-inspector-title">Version {selectedVersion.version} questions</h3>
                    <p>{selectedVersion.questions.length} questions across {selectedVersionSections.length} sections.</p>
                  </div>
                  <div className="section-actions">
                    {selectedVersion.status === 'draft' && canManage ? <button className="button secondary small" onClick={() => beginVersion(selectedVersion)} type="button"><Pencil size={14} /> Edit questions</button> : null}
                    {selectedVersion.status === 'draft' && isPlatformAdmin ? <button className="button primary small" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate(selectedVersion)} type="button"><Check size={14} /> Publish</button> : null}
                  </div>
                </div>
                {selectedVersion.questions.length ? (
                  <div className="survey-question-sections">
                    {selectedVersionSections.map(([section, sectionQuestions]) => (
                      <section className="survey-question-section" key={section}>
                        <div className="survey-question-section-heading"><h4>{section}</h4><span>{sectionQuestions.length} questions</span></div>
                        <div className="survey-question-preview-list">
                          {sectionQuestions.map((question) => {
                            const visibility = visibilityDescription(question, selectedVersion.questions);
                            return (
                              <article className="survey-question-preview" key={question.id}>
                                <span className="survey-question-position">{question.position}</span>
                                <div className="survey-question-preview-body">
                                  <div className="survey-question-preview-heading">
                                    <div><h5>{question.label}</h5>{question.help_text ? <p>{question.help_text}</p> : null}</div>
                                    {isPlatformAdmin && selectedVersion.status === 'published' ? <button aria-label={`Correct wording for ${question.label}`} className="text-button" onClick={() => beginWordingCorrection(selectedVersion, question)} type="button"><Pencil size={14} /> Correct wording</button> : null}
                                  </div>
                                  <div className="survey-question-badges">
                                    <span>{questionKindLabel(question.kind)}</span>
                                    <span className={question.required ? 'required' : ''}>{question.required ? 'Required' : 'Optional'}</span>
                                    <code>{question.key}</code>
                                  </div>
                                  {question.choices.length ? <div className="survey-question-choices" aria-label="Answer choices">{question.choices.map((choice) => <span key={choice.value}>{choice.label}<small>{choice.value}</small></span>)}</div> : null}
                                  {visibility ? <p className="survey-question-visibility">{visibility}</p> : null}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                ) : <p className="survey-question-empty">This draft does not contain questions yet.</p>}
              </section>
            ) : null}
          </div>
        </DataPanel>
      </section>
    ) : null}
    {editing ? <div className="confirmation-backdrop" role="presentation"><section aria-labelledby="survey-template-form-title" aria-modal="true" className="confirmation-dialog survey-template-dialog" role="dialog"><button aria-label="Close survey template form" className="icon-button confirmation-close" disabled={templateMutation.isPending} onClick={() => setEditing(null)} type="button"><X size={19} /></button><div className="card-heading"><div><h2 id="survey-template-form-title">{editing === 'new' ? 'New survey template' : `Edit ${editing.name}`}</h2><p>Use a clear title and stable slug. The reporting preset is optional; leave it blank for a generic dashboard.</p></div></div><form className="management-form" onSubmit={submitTemplate}><div className="form-grid"><label className="form-grid-full">Client<select disabled={editing !== 'new'} required value={draft.client} onChange={(event) => setDraft({ ...draft, client: event.target.value, brand: '' })}><option value="">Select client</option>{clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label><label className="form-grid-full">Brand (optional)<select disabled={!draft.client || brands.isLoading} value={draft.brand} onChange={(event) => setDraft({ ...draft, brand: event.target.value })}><option value="">All brands</option>{brandOptions.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}</select>{brands.isError ? <small className="form-field-error">Unable to load brands for this client.</small> : null}</label><label>Template name<input required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label><label>Stable slug<input pattern="[a-z0-9]+(?:-[a-z0-9]+)*" required value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: event.target.value.toLowerCase().replaceAll(' ', '-') })} /></label><label className="form-grid-full">Reporting preset<select value={draft.reporting_preset} onChange={(event) => setDraft({ ...draft, reporting_preset: event.target.value })}><option value="">Generic dashboard</option>{presetOptions.filter((preset) => preset.is_active || preset.key === draft.reporting_preset).map((preset) => <option key={preset.id} value={preset.key}>{preset.name}</option>)}</select>{presets.isError ? <small className="form-field-error">Unable to load reporting presets.</small> : null}</label><label className="form-grid-full">Description<textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label><label className="checkbox-field"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for new survey campaigns</label></div>{templateMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(templateMutation.error)}</p> : null}<div className="form-actions"><button className="button secondary" disabled={templateMutation.isPending} onClick={() => setEditing(null)} type="button">Cancel</button><button className="button primary" disabled={templateMutation.isPending} type="submit">{templateMutation.isPending ? <InlineSpinner /> : null}<Save size={16} /> Save template</button></div></form></section></div> : null}
    {editingPreset && presetDraft ? <div className="confirmation-backdrop" role="presentation"><section aria-labelledby="survey-preset-form-title" aria-modal="true" className="confirmation-dialog survey-version-dialog" role="dialog"><button aria-label="Close reporting preset form" className="icon-button confirmation-close" disabled={presetMutation.isPending} onClick={() => { setEditingPreset(null); setPresetDraft(null); }} type="button"><X size={19} /></button><div className="card-heading"><div><h2 id="survey-preset-form-title">Reporting presets</h2><p>Chart choices apply to the web dashboard. Stable-key mappings control grouped dashboard results and XLSX headings.</p></div></div><form className="management-form" onSubmit={(event) => { event.preventDefault(); presetMutation.mutate(); }}><div className="form-grid"><label className="form-grid-full">Preset<select value={editingPreset.id} onChange={(event) => { const preset = presetOptions.find((item) => item.id === event.target.value); if (preset) beginPreset(preset); }}>{presetOptions.map((preset) => <option key={preset.id} value={preset.id}>{preset.name} · {preset.key}</option>)}</select></label><label className="form-grid-full">Name<input required value={presetDraft.name} onChange={(event) => setPresetDraft({ ...presetDraft, name: event.target.value })} /></label><label>Availability mix chart<select value={presetDraft.availability_chart_type} onChange={(event) => setPresetDraft({ ...presetDraft, availability_chart_type: event.target.value as SurveyReportChartType })}><option value="donut">Donut</option><option value="pie">Pie</option><option value="bar">Bar</option></select></label><label>Question distribution chart<select value={presetDraft.question_chart_type} onChange={(event) => setPresetDraft({ ...presetDraft, question_chart_type: event.target.value as SurveyReportChartType })}><option value="bar">Bar</option><option value="donut">Donut</option><option value="pie">Pie</option></select></label><label className="form-grid-full">Description<textarea value={presetDraft.description} onChange={(event) => setPresetDraft({ ...presetDraft, description: event.target.value })} /></label><ReportingPresetColourFields draft={presetDraft} onChange={(chart_colors) => setPresetDraft({ ...presetDraft, chart_colors })} /><label className="form-grid-full">Grouped headings and question mappings <small>Advanced JSON</small><textarea className="survey-preset-json" required value={presetDraft.reporting_groups} onChange={(event) => setPresetDraft({ ...presetDraft, reporting_groups: event.target.value })} /></label><label className="form-grid-full">Availability sections <small>Advanced JSON</small><textarea className="survey-preset-json" required value={presetDraft.availability_sections} onChange={(event) => setPresetDraft({ ...presetDraft, availability_sections: event.target.value })} /></label><label className="checkbox-field"><input checked={presetDraft.is_active} type="checkbox" onChange={(event) => setPresetDraft({ ...presetDraft, is_active: event.target.checked })} /> Available for survey templates</label></div>{presetMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(presetMutation.error)}</p> : null}<div className="form-actions"><button className="button secondary" disabled={presetMutation.isPending} onClick={() => { setEditingPreset(null); setPresetDraft(null); }} type="button">Cancel</button><button className="button primary" disabled={presetMutation.isPending} type="submit">{presetMutation.isPending ? <InlineSpinner /> : <Save size={16} />} Save preset</button></div></form></section></div> : null}
    {editingVersion ? <div className="confirmation-backdrop" role="presentation"><section aria-labelledby="survey-version-form-title" aria-modal="true" className="confirmation-dialog survey-version-dialog" role="dialog"><button aria-label="Close survey version form" className="icon-button confirmation-close" disabled={versionMutation.isPending} onClick={() => setEditingVersion(null)} type="button"><X size={19} /></button><div className="card-heading"><div><h2 id="survey-version-form-title">{editingVersion === 'new' ? 'Draft questionnaire version' : `Edit version ${editingVersion.version}`}</h2><p>Question keys must remain stable once published. Select options use one `value | label` per line.</p></div></div><form className="management-form survey-question-editor" onSubmit={submitVersion}>{editingVersion === 'new' ? <label>Version notes<textarea value={versionNotes} onChange={(event) => setVersionNotes(event.target.value)} /></label> : null}<section aria-labelledby="survey-question-csv-title" className="survey-question-csv"><div><h3 id="survey-question-csv-title">Question CSV</h3><p>Download the controlled format, edit it in a spreadsheet, then load it here. Loading replaces the unsaved questions below; nothing is saved until you save this draft.</p></div><div className="survey-question-csv-actions"><button className="button secondary" disabled={questionCsvTemplateMutation.isPending} onClick={() => questionCsvTemplateMutation.mutate()} type="button">{questionCsvTemplateMutation.isPending ? <InlineSpinner /> : <Download size={16} />} Download CSV template</button><label className="button secondary"><Upload size={16} /> Choose CSV<input accept=".csv,text/csv" aria-label="Survey question CSV" className="survey-question-csv-file" type="file" onChange={(event) => { setQuestionCsvFile(event.target.files?.[0] ?? null); setQuestionCsvError(null); setQuestionCsvNotice(null); }} /></label><button className="button secondary" disabled={!questionCsvFile} onClick={() => { void loadQuestionCsv(); }} type="button">Load CSV into draft</button></div>{questionCsvFile ? <p className="survey-question-csv-selected">Selected: {questionCsvFile.name}</p> : null}{questionCsvNotice ? <p className="success-banner" role="status">{questionCsvNotice}</p> : null}{questionCsvError || questionCsvTemplateMutation.isError ? <p className="form-error" role="alert">{questionCsvError ?? getApiErrorMessage(questionCsvTemplateMutation.error)}</p> : null}</section><div className="survey-question-list">{questions.map((question, index) => <fieldset className="survey-question-card" key={`${question.key}-${index}`}><legend>Question {index + 1}</legend><div className="form-grid"><label>Stable key<input pattern="[a-z0-9]+(?:_[a-z0-9]+)*" required value={question.key} onChange={(event) => updateQuestion(index, { key: event.target.value.toLowerCase().replaceAll(' ', '_') })} /></label><label>Section<input value={question.section} onChange={(event) => updateQuestion(index, { section: event.target.value })} /></label><label className="form-grid-full">Question<input required value={question.label} onChange={(event) => updateQuestion(index, { label: event.target.value })} /></label><label className="form-grid-full">Help text<textarea value={question.help_text} onChange={(event) => updateQuestion(index, { help_text: event.target.value })} /></label><label>Answer type<select value={question.kind} onChange={(event) => updateQuestion(index, { kind: event.target.value as SurveyQuestionKind, choices_text: isSelect(event.target.value as SurveyQuestionKind) ? question.choices_text : '' })}>{questionKinds.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}</select></label><label className="checkbox-field"><input checked={question.required} type="checkbox" onChange={(event) => updateQuestion(index, { required: event.target.checked })} /> Required at submission</label>{isSelect(question.kind) ? <label className="form-grid-full">Options<textarea aria-label={`Options for question ${index + 1}`} placeholder={'available | Available\nnot_available | Not available'} required value={question.choices_text} onChange={(event) => updateQuestion(index, { choices_text: event.target.value })} /></label> : null}<label>Show when (optional)<select value={question.condition_question} onChange={(event) => updateQuestion(index, { condition_question: event.target.value })}><option value="">Always show</option>{questions.slice(0, index).map((previous) => <option key={previous.key} value={previous.key}>{previous.label || previous.key}</option>)}</select></label>{question.condition_question ? <label>Expected value<input required value={question.condition_value} onChange={(event) => updateQuestion(index, { condition_value: event.target.value })} /></label> : null}</div><button aria-label={`Remove question ${index + 1}`} className="text-button survey-remove-question" disabled={questions.length === 1} onClick={() => setQuestions((current) => current.filter((_, questionIndex) => questionIndex !== index))} type="button"><X size={14} /> Remove question</button></fieldset>)}</div><button className="button secondary" onClick={() => setQuestions((current) => [...current, emptyQuestion(current.length + 1)])} type="button"><Plus size={16} /> Add question</button>{versionMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(versionMutation.error)}</p> : null}<div className="form-actions"><button className="button secondary" disabled={versionMutation.isPending} onClick={() => setEditingVersion(null)} type="button">Cancel</button><button className="button primary" disabled={versionMutation.isPending} type="submit">{versionMutation.isPending ? <InlineSpinner /> : null}<Save size={16} /> Save draft</button></div></form></section></div> : null}
    {correctingQuestion ? (
      <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeWordingCorrection(); }} role="presentation">
        <section aria-labelledby="survey-wording-correction-title" aria-modal="true" className="confirmation-dialog survey-template-dialog" role="dialog">
          <button aria-label="Close wording correction" className="icon-button confirmation-close" disabled={wordingCorrectionMutation.isPending} onClick={closeWordingCorrection} type="button"><X size={19} /></button>
          <div className="card-heading"><div><h2 id="survey-wording-correction-title">Correct question wording</h2><p>This is an append-only display-copy correction for Version {correctingQuestion.version.version}. Existing answers keep their original version and remain unchanged.</p></div></div>
          <form className="management-form" onSubmit={submitWordingCorrection}>
            <div className="form-grid">
              <label className="form-grid-full">Question wording<input required maxLength={500} value={wordingCorrectionDraft.corrected_label} onChange={(event) => setWordingCorrectionDraft((current) => ({ ...current, corrected_label: event.target.value }))} /></label>
              <label className="form-grid-full">Help text <small>Optional</small><textarea maxLength={4000} value={wordingCorrectionDraft.corrected_help_text} onChange={(event) => setWordingCorrectionDraft((current) => ({ ...current, corrected_help_text: event.target.value }))} /></label>
              <label className="form-grid-full">Reason for correction<textarea required maxLength={500} placeholder="e.g. Corrected a spelling error in the product name" value={wordingCorrectionDraft.reason} onChange={(event) => setWordingCorrectionDraft((current) => ({ ...current, reason: event.target.value }))} /></label>
            </div>
            {wordingCorrectionMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(wordingCorrectionMutation.error)}</p> : null}
            <div className="form-actions"><button className="button secondary" disabled={wordingCorrectionMutation.isPending} onClick={closeWordingCorrection} type="button">Cancel</button><button className="button primary" disabled={wordingCorrectionMutation.isPending} type="submit">{wordingCorrectionMutation.isPending ? <InlineSpinner /> : <Save size={16} />} Save correction</button></div>
          </form>
        </section>
      </div>
    ) : null}
  </>;
}
